import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, AlertTriangle, History, PlusCircle, Download } from 'lucide-react';
import { toast } from 'sonner';

import API from '../api_config';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
};

export default function WastagePage() {
  const [wastages, setWastages] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Search state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchProduct, setSearchProduct] = useState('');
  const [searchWarehouse, setSearchWarehouse] = useState('');
  const [searchReason, setSearchReason] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    product_id: '',
    warehouse_id: '',
    quantity: '',
    reason: 'daño',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) params.append('end_date', new Date(endDate).toISOString());
      if (searchProduct) params.append('product_id', searchProduct);
      if (searchWarehouse) params.append('warehouse_id', searchWarehouse);
      if (searchReason) params.append('reason', searchReason);

      const [wastageRes, productRes, warehouseRes] = await Promise.all([
        axios.get(`${API}/wastage?${params.toString()}`),
        axios.get(`${API}/products`),
        axios.get(`${API}/warehouses`)
      ]);
      setWastages(wastageRes.data);
      setProducts(productRes.data.sort((a,b) => a.name.localeCompare(b.name)));
      setWarehouses(warehouseRes.data);
      if (warehouseRes.data.length > 0 && !formData.warehouse_id) {
          setFormData(prev => ({...prev, warehouse_id: warehouseRes.data[0].id}));
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  }, [startDate, endDate, searchProduct, searchWarehouse, searchReason, formData.warehouse_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) params.append('end_date', new Date(endDate).toISOString());
      if (searchProduct) params.append('product_id', searchProduct);
      if (searchWarehouse) params.append('warehouse_id', searchWarehouse);
      if (searchReason) params.append('reason', searchReason);
      
      const response = await axios.get(`${API}/wastage/export/excel?${params.toString()}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'reporte_mermas.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exportado correctamente');
    } catch (error) {
      toast.error('Error al exportar Excel');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_id || !formData.warehouse_id || !formData.quantity) {
      toast.error('Complete los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/wastage`, {
        ...formData,
        quantity: parseFloat(formData.quantity)
      });
      toast.success('Merma registrada exitosamente');
      setFormData({
        product_id: '',
        warehouse_id: warehouses[0]?.id || '',
        quantity: '',
        reason: 'daño',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="wastage-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
            <div className="p-3 bg-red-600 rounded-2xl">
            <Trash2 className="h-8 w-8 text-white" />
            </div>
            <div>
            <h1 className="text-4xl font-bold tracking-tight">Mermas y Pérdidas</h1>
            <p className="text-muted-foreground mt-1">Registra productos dañados, vencidos o perdidos</p>
            </div>
        </div>
        <Button onClick={handleExport} variant="outline" className="border-green-600 text-green-700 hover:bg-green-50">
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Registration Form */}
        <Card className="lg:col-span-1 border-red-100 dark:border-red-900/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
                <PlusCircle className="h-5 w-5 text-red-500" />
                Registrar Merma
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Producto</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formData.product_id}
                  onChange={(e) => setFormData({...formData, product_id: e.target.value})}
                  required
                >
                  <option value="">Seleccione producto...</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Bodega</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={formData.warehouse_id}
                  onChange={(e) => setFormData({...formData, warehouse_id: e.target.value})}
                  required
                >
                  <option value="">Seleccione bodega...</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cantidad</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Motivo</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={formData.reason}
                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  >
                    <option value="daño">Dañado</option>
                    <option value="vencimiento">Vencido</option>
                    <option value="robo">Robo / Extravío</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notas Adicionales</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Detalles del incidente..."
                />
              </div>

              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading}>
                {loading ? 'Procesando...' : 'Confirmar Merma'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* History List */}
        <div className="lg:col-span-2 space-y-4">
            {/* Filters */}
            <Card className="border-blue-100 bg-blue-50/30">
                <CardContent className="pt-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        <div className="space-y-1">
                            <Label className="text-xs">Inicio</Label>
                            <Input type="date" className="h-8 text-xs" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Fin</Label>
                            <Input type="date" className="h-8 text-xs" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Producto</Label>
                            <select className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs" value={searchProduct} onChange={(e) => setSearchProduct(e.target.value)}>
                                <option value="">Todos</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Bodega</Label>
                            <select className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs" value={searchWarehouse} onChange={(e) => setSearchWarehouse(e.target.value)}>
                                <option value="">Todas</option>
                                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Motivo</Label>
                            <select className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs" value={searchReason} onChange={(e) => setSearchReason(e.target.value)}>
                                <option value="">Todos</option>
                                <option value="daño">Dañado</option>
                                <option value="vencimiento">Vencido</option>
                                <option value="robo">Robo</option>
                                <option value="ajuste_inventario">Inventario</option>
                                <option value="otro">Otro</option>
                            </select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <History className="h-5 w-5 text-muted-foreground" />
                    Historial de Mermas
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="rounded-md border overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted text-muted-foreground uppercase text-[10px] font-bold">
                    <tr>
                        <th className="p-3 text-left">Fecha</th>
                        <th className="p-3 text-left">Producto</th>
                        <th className="p-3 text-center">Cant.</th>
                        <th className="p-3 text-left">Motivo</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y">
                    {wastages.length === 0 ? (
                        <tr>
                        <td colSpan="4" className="p-8 text-center text-muted-foreground">No hay registros de mermas</td>
                        </tr>
                    ) : (
                        wastages.map((w) => {
                        const prod = products.find(p => p.id === w.product_id);
                        return (
                            <tr key={w.id} className="hover:bg-muted/50 transition-colors">
                            <td className="p-3 whitespace-nowrap">
                                {new Date(w.created_at).toLocaleDateString('es-CL')}
                                <span className="block text-[10px] text-muted-foreground">
                                    {new Date(w.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                            </td>
                            <td className="p-3">
                                <span className="font-medium text-foreground">{prod?.name || 'Cargando...'}</span>
                                <span className="block text-[10px] font-mono text-muted-foreground">{prod?.sku}</span>
                            </td>
                            <td className="p-3 text-center font-mono font-bold text-red-600">
                                -{w.quantity}
                            </td>
                            <td className="p-3">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                    w.reason === 'robo' ? 'bg-orange-100 text-orange-700' : 
                                    w.reason === 'vencimiento' ? 'bg-blue-100 text-blue-700' :
                                    w.reason === 'ajuste_inventario' ? 'bg-purple-100 text-purple-700' :
                                    'bg-red-100 text-red-700'
                                }`}>
                                    {w.reason === 'ajuste_inventario' ? 'Inventario' : w.reason}
                                </span>
                                {w.notes && <p className="text-[10px] text-muted-foreground mt-1 italic">"{w.notes}"</p>}
                            </td>
                            </tr>
                        );
                        })
                    )}
                    </tbody>
                </table>
                </div>
            </CardContent>
            </Card>
        </div>
      </div>

      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-4 rounded-lg flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm">
            <p className="font-bold text-amber-800 dark:text-amber-400">Importante:</p>
            <p className="text-amber-700 dark:text-amber-500">
                Al registrar una merma, el stock del producto se descontará automáticamente de la bodega seleccionada. 
                Esta acción no genera un registro de venta, sino un ajuste negativo de inventario.
            </p>
        </div>
      </div>
    </div>
  );
}
